import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRegisterUser } from "../hooks/useRegisterUser";
import { Country, State, City } from "country-state-city";

// Add this CSS in your component or in your stylesheet
const selectStyle = {
  height: "48px", // Match input height
  fontSize: "1.6rem", // Match input font size
  appearance: "none", // Remove default select appearance
  WebkitAppearance: "none",
  MozAppearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 0.75rem center",
  backgroundSize: "1em",
  paddingRight: "2.5rem",
};

const imagePreviewStyle = {
  width: "100px",
  height: "100px",
  borderRadius: "50%",
  objectFit: "cover",
  border: "2px solid #primary",
  margin: "0 auto 20px auto",
  display: "block",
};

const uploadButtonStyle = {
  position: "relative",
  overflow: "hidden",
  display: "block",
  width: "100%",
};

const fileInputStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  opacity: 0,
  width: "100%",
  height: "100%",
  cursor: "pointer",
};

const avatarUploadStyles = {
  avatarUpload: {
    position: "relative",
    maxWidth: "120px",
    margin: "0 auto",
  },
  avatarEdit: {
    position: "absolute",
    bottom: "0",
    right: "0",
    zIndex: "1",
    marginRight: "12px",
  },
  uploadLabel: {
    width: "32px",
    height: "32px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#e6f0ff",
    color: "#0066ff",
    border: "1px solid #0066ff",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  },
  avatarPreview: {
    width: "120px",
    height: "120px",
    position: "relative",
    borderRadius: "50%",
    border: "2px solid #primary",
    overflow: "hidden",
  },
  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },
};

export const SignUpForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    number: "",
    password: "",
    profileImage: null,
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { loading, error, resData, registerUser } = useRegisterUser();
  const navigate = useNavigate();

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format.";
    }
    if (!formData.address.trim()) newErrors.address = "Address is required.";
    if (!formData.country) newErrors.country = "Country is required.";
    if (!formData.state) newErrors.state = "State is required.";
    if (!formData.city) newErrors.city = "City is required.";
    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required.";
    } else if (formData.pincode.length !== 6) {
      newErrors.pincode = "Pincode must be 6 digits.";
    }
    if (!formData.number.trim()) {
      newErrors.number = "Phone number is required.";
    } else if (formData.number.length !== 10) {
      newErrors.number = "Phone number must be 10 digits.";
    }
    if (!formData.password.trim()) newErrors.password = "Password is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "number") {
      // Limit phone number to 10 digits
      const numberValue = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prevData) => ({
        ...prevData,
        [name]: numberValue,
      }));
      // Clear error when valid input is provided
      if (numberValue.length === 10) {
        setErrors((prev) => ({ ...prev, number: "" }));
      }
    } else if (name === "pincode") {
      // Limit pincode to 6 digits
      const pincodeValue = value.replace(/\D/g, "").slice(0, 6);
      setFormData((prevData) => ({
        ...prevData,
        [name]: pincodeValue,
      }));
      // Clear error when valid input is provided
      if (pincodeValue.length === 6) {
        setErrors((prev) => ({ ...prev, pincode: "" }));
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
      // Clear error for other fields when they have a value
      if (value.trim()) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevData) => ({
        ...prevData,
        profileImage: file,
      }));

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Convert isoCode to names
    const countryName = countries.find(
      (country) => country.isoCode === formData.country
    )?.name;

    const stateName = states.find(
      (state) => state.isoCode === formData.state
    )?.name;

    const cityName = cities.find((city) => city.name === formData.city)?.name;

    // Create FormData object
    const formDataToSend = new FormData();

    // Append all text fields
    formDataToSend.append("name", formData.name);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("password", formData.password);
    formDataToSend.append("address", formData.address);
    formDataToSend.append("city", cityName || formData.city);
    formDataToSend.append("state", stateName || formData.state);
    formDataToSend.append("country", countryName || formData.country);
    formDataToSend.append("pincode", formData.pincode);
    formDataToSend.append("number", formData.number);

    // Append profile image if exists
    if (formData.profileImage) {
      formDataToSend.append("profileImage", formData.profileImage);
    }

    console.log("Submitting form data:", Object.fromEntries(formDataToSend));
    await registerUser(formDataToSend);
  };

  useEffect(() => {
    if (resData?.success) {
      navigate("/verify-email");
    }
  }, [resData, navigate]);

  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  useEffect(() => {
    if (formData.country) {
      const countryStates = State.getStatesOfCountry(formData.country);
      setStates(countryStates);
      setFormData((prev) => ({
        ...prev,
        state: "",
        city: "",
      }));
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.country && formData.state) {
      const stateCities = City.getCitiesOfState(
        formData.country,
        formData.state
      );
      setCities(stateCities);
      setFormData((prev) => ({
        ...prev,
        city: "",
      }));
    }
  }, [formData.country, formData.state]);

  return (
    <section className="auth bg-light d-flex flex-wrap align-items-center justify-content-center min-vh-100">
      {/* Fixed Image Area */}
      <div
        className="auth-left d-none d-lg-flex align-items-center justify-content-center"
        style={{
          width: "50%",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
        }}
      >
        <img
          // src="assets/images/portfolio/home-port-2.jpg"
          src="assets/images/portfolio/signup9.png"
          alt="Authentication"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>

      {/* Scrollable Form Area */}
      <div
        className="auth-right col-12 col-lg-6 py-5 px-4 d-flex flex-column justify-content-start align-items-center"
        style={{
          // width: "50%",
          // height: "100vh",
          overflowY: "scroll",
          paddingTop: "10px",
          paddingRight: "20px",
          marginLeft: "auto",
        }}
      >
        <div className="mx-auto mx-lg-0 w-100" style={{ maxWidth: "464px" }}>
          <div className="text-center mb-4">
            <Link to="/">
              <img
                src="assets/images/logo/V2V Logo 188x80.png"
                alt="Logo"
                className="img-fluid mb-3"
                style={{ maxWidth: "290px" }}
              />
            </Link>
            <h4 className="mb-2">Create Your Account</h4>
            <p className="text-secondary">
              Join us by entering your details below.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* <div className="text-center mb-4">
              <div style={avatarUploadStyles.avatarUpload}>
                <div style={avatarUploadStyles.avatarEdit}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    id="imageUpload"
                    hidden
                  />
                  <label
                    htmlFor="imageUpload"
                    style={avatarUploadStyles.uploadLabel}
                  >
                    <Icon icon="solar:camera-outline" />
                  </label>
                </div>
                <div style={avatarUploadStyles.avatarPreview}>
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Profile Preview"
                      style={avatarUploadStyles.previewImage}
                    />
                  ) : (
                    <div
                      style={{
                        ...avatarUploadStyles.previewImage,
                        backgroundColor: "#f0f0f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon
                        icon="mdi:account"
                        style={{ fontSize: "40px", color: "#666" }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div> */}

            <div className="position-relative mb-3 d-flex flex-column align-items-start">
              <div className="position-relative w-100">
                <Icon
                  icon="f7:person"
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                />
                <input
                  type="text"
                  className="form-control form-control-lg ps-5 border rounded-3"
                  placeholder="Name"
                  name="name"
                  onChange={handleChange}
                />
              </div>
              {errors.name && (
                <p className="text-danger fs-5 mt-1">{errors.name}</p>
              )}
            </div>

            <div className="position-relative mb-3 d-flex flex-column align-items-start">
              <div className="position-relative w-100">
                <Icon
                  icon="mage:email"
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                />
                <input
                  type="email"
                  className="form-control form-control-lg ps-5 border border-gray rounded-3"
                  placeholder="Email"
                  name="email"
                  onChange={handleChange}
                />
              </div>
              {errors.email && (
                <p className="text-danger fs-5 mt-1">{errors.email}</p>
              )}
            </div>

            {/* Address Field */}
            <div className="position-relative mb-3 d-flex flex-column align-items-start">
              <div className="position-relative w-100">
                <Icon
                  icon="mdi:home"
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                />
                <input
                  type="text"
                  className="form-control form-control-lg ps-5 border border-gray rounded-3"
                  placeholder="Address"
                  name="address"
                  onChange={handleChange}
                  value={formData.address}
                />
              </div>
              {errors.address && (
                <p className="text-danger fs-5 mt-1">{errors.address}</p>
              )}
            </div>

            {/* Country and State Side by Side */}
            <div className="row g-3 mb-3">
              <div className="col-md-6 position-relative">
                <Icon
                  icon="mdi:earth"
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                />
                <select
                  className="form-control form-control-lg ps-5 border border-gray rounded-3"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  style={selectStyle}
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.isoCode} value={country.isoCode}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {errors.country && (
                  <p className="text-danger fs-5 mt-1">{errors.country}</p>
                )}
              </div>

              <div className="col-md-6 position-relative">
                <Icon
                  icon="mdi:map-marker"
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                />
                <select
                  className="form-control form-control-lg ps-5 border border-gray rounded-3"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  disabled={!formData.country}
                  style={selectStyle}
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-danger fs-5 mt-1">{errors.state}</p>
                )}
              </div>
            </div>

            {/* City and Pincode Side by Side */}
            <div className="row g-3 mb-3">
              <div className="col-md-6 position-relative">
                <Icon
                  icon="mdi:city"
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                />
                <select
                  className="form-control form-control-lg ps-5 border border-gray rounded-3"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={!formData.state}
                  style={selectStyle}
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <p className="text-danger fs-5 mt-1">{errors.city}</p>
                )}
              </div>
              <div className="col-md-6 position-relative  d-flex flex-column align-items-start">
                <div className="col-md-6 w-100 position-relative">
                  <Icon
                    icon="mdi:pin"
                    className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                  />
                  <input
                    type="text"
                    className="form-control form-control-lg ps-5 border border-gray rounded-3"
                    placeholder="Pincode"
                    name="pincode"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    onChange={handleChange}
                    value={formData.pincode}
                  />
                </div>
                {errors.pincode && (
                  <p className="text-danger fs-5 mt-1">{errors.pincode}</p>
                )}
              </div>
            </div>

            {/* Phone Number and Password Fields Row */}
            <div className="row g-3 mb-3">
              {/* Phone Number Field */}
              <div className="col-md-6 position-relative d-flex flex-column align-items-start">
                <div className="position-relative w-100">
                  <Icon
                    icon="mdi:phone"
                    className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                  />
                  <input
                    type="tel"
                    className="form-control form-control-lg ps-5 border border-gray rounded-3"
                    placeholder="Phone Number"
                    name="number"
                    pattern="[0-9]*"
                    inputMode="numeric"
                    onChange={handleChange}
                    value={formData.number}
                  />
                </div>
                {errors.number && (
                  <p className="text-danger fs-5 mt-1">{errors.number}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="col-md-6 position-relative d-flex flex-column align-items-start">
                <div className="position-relative w-100">
                  <Icon
                    icon="solar:lock-password-outline"
                    className="position-absolute top-50 start-0 translate-middle-y ms-3 fs-4 text-secondary"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control form-control-lg ps-5 pe-5 border border-gray rounded-3"
                    placeholder="Password"
                    name="password"
                    onChange={handleChange}
                  />
                  <Icon
                    icon={showPassword ? "mdi:eye-off" : "mdi:eye"}
                    className="position-absolute top-50 end-0 translate-middle-y me-3 fs-4 text-secondary cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ cursor: "pointer" }}
                  />
                </div>
                {errors.password && (
                  <p className="text-danger fs-5 mt-1">{errors.password}</p>
                )}
              </div>
            </div>
            {error && <p className="text-danger fs-4 mt-3">{error.message}</p>}

            <div className="form-group">
              <button
                type="submit"
                className={`btn btn-primary btn-lg fs-3 rounded-2 mb-3 ${
                  loading ? "opacity-50 cursor-not-allowed disabled" : ""
                }`}
                style={{
                  width: "100%",
                  maxWidth: "464px",
                  display: "block",
                  margin: "0 auto",
                }}
              >
                {loading ? "Loading..." : "Sign Up"}
              </button>
            </div>

            <div className="text-center mt-3">
              <p className="mb-0">
                Already have an account?
                <Link to="https://app.v2vbn.org/login" className="text-primary">
                  {" "}
                  Sign In
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
